import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { ingredientsService, IngredientCreateEntry } from '../services/ingredientsService';
import { Ingredient } from 'public-contracts';
import { RecipeCombobox } from '../components/RecipeCombobox';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';

// Lucide React icons
import {
  Beaker,
  Save,
  AlertTriangle,
  ChefHat,
} from 'lucide-react';

interface IngredientFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (ingredient: Ingredient) => void;
  ingredient?: Ingredient | null;
}

export const ModernIngredientForm: React.FC<IngredientFormProps> = ({
  open,
  onClose,
  onSave,
  ingredient,
}) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<IngredientCreateEntry>({
    name: '',
    description: '',
    imageId: '',
    recipeId: undefined,
  });

  useEffect(() => {
    if (open) {
      if (ingredient) {
        setFormData({
          name: ingredient.name,
          description: ingredient.description || '',
          imageId: ingredient.imageId || '',
          recipeId: ingredient.recipeId,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          imageId: '',
          recipeId: undefined,
        });
      }
    }
  }, [open, ingredient]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let savedIngredient: Ingredient;

      if (ingredient) {
        savedIngredient = await ingredientsService.updateIngredient(ingredient.id, formData);
      } else {
        savedIngredient = await ingredientsService.createIngredient(formData);
      }

      onSave(savedIngredient);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save ingredient');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof IngredientCreateEntry, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    return formData.name && formData.description;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Beaker className="h-5 w-5" />
            <span>
              {ingredient
                ? translate('pos.ingredients.edit', 'Edit Ingredient')
                : translate('pos.ingredients.create', 'Create Ingredient')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {ingredient
              ? 'Update ingredient details'
              : 'Create a new ingredient'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {translate('pos.ingredients.fields.name', 'Name')} *
                </Label>
                <Input
                  id="name"
                  placeholder="Enter ingredient name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {translate('pos.ingredients.fields.description', 'Description')} *
                </Label>
                <Textarea
                  id="description"
                  placeholder="Enter ingredient description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="imageId">
                  {translate('pos.ingredients.fields.imageId', 'Image ID')}
                </Label>
                <Input
                  id="imageId"
                  placeholder="Enter image ID (optional)"
                  value={formData.imageId}
                  onChange={(e) => handleInputChange('imageId', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipeId">
                  {translate('pos.ingredients.fields.recipeId', 'Recipe (Optional)')}
                </Label>
                <RecipeCombobox
                  value={formData.recipeId}
                  onValueChange={(value) => handleInputChange('recipeId', value || undefined)}
                  placeholder={translate(
                    'pos.ingredients.placeholders.selectRecipe',
                    'Select recipe (optional)'
                  )}
                  allowNone={true}
                  noneLabel={translate('pos.ingredients.recipe.none', 'No recipe')}
                />
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.ingredients.recipe.help',
                    'If this ingredient is composed from other ingredients, select a recipe. This allows nested recipes up to 2 levels.'
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={!isFormValid() || loading}>
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {translate('common.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {translate('common.save', 'Save')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
